/**
 * Phase 5B.2 — Customer CRM page. Shared by both:
 *   /owner/crm                              (role="owner")
 *   /admin/brands/:brand_id/crm             (role="admin")
 *
 * The component is brand-scoped via the `brandId` prop. For owner role,
 * brand_id is derived from the JWT by the server (we still carry it in
 * the query key for cache isolation per `qk` contract). For admin role,
 * every API call appends `?brand_id=<brandId>` and may pass
 * `&include_fixture=true` via the toggle.
 *
 * Mandates upheld:
 *   - Query keys ONLY from qk factory (Mandate 2 — cache isolation).
 *   - Server-derived brand context — never trust client-supplied
 *     brand_id for owner endpoints.
 *   - All mutations invalidate the relevant tuples (detail + list + kpi).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpDown, Calendar, CheckCircle2, ChevronDown, ChevronLeft,
  ChevronRight, Crown, Download, Filter, Gift, MapPin, MessageCircle, MoreHorizontal,
  Pencil, RefreshCw, Search, Square, SquareCheck, Star, Trash2, Users, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  formatDate, formatDateTime, formatRupiah, timeAgo,
} from '@/lib/utils';
import {
  PageHeader, SectionCard, EmptyState, FullSpinner, QueryError,
  StatusPill, SkeletonLines,
} from '@/components/ui_primitives/Misc';
import KpiCard from '@/components/ui_primitives/KpiCard';
import {
  Table, THead, TBody, Tr, Th, Td,
} from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import { FieldRow, Input, Select } from '@/components/ui_primitives/Field';
import Drawer from '@/components/ui_primitives/Drawer';
import Modal from '@/components/ui_primitives/Modal';
import TierBadge from '@/components/membership/TierBadge';
import AssignMembershipModal from '@/components/membership/AssignMembershipModal';
import { rupiah, formatDate as fmtDate, expiryLabel, STATUS_PILL as MEMBERSHIP_STATUS_PILL } from '@/lib/membershipFmt';
import BonusStampModal, { canGrantBonus, BONUS_SOURCES } from '@/components/BonusStampModal';
import TransactionDetailsModal from '@/components/TransactionDetailsModal';

// L4 (2026-06-25) — friendly labels for BONUS source enum shown on the
// standalone-bonus transaction rows in the drawer's Transactions tab.
const BONUS_SOURCE_LABEL = Object.fromEntries(
  BONUS_SOURCES.map((s) => [s.value, s.label]),
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const DEFAULT_PAGE_SIZE = 25;

const STATUS_DOT = {
  active: { color: '#34D399', label: 'Active', variant: 'green' },
  at_risk: { color: '#F59E0B', label: 'At risk', variant: 'amber' },
  inactive: { color: '#F87171', label: 'Inactive', variant: 'red' },
};

const SORT_LABELS = {
  spend: 'Total Spending',
  transactions: 'Total Transactions',
  stamps: 'Total Stamps',
  last_visit: 'Last Visit',
  name: 'Name',
  joined: 'Joined Date',
};

function useDebounce(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function Initials({ name, isVip }) {
  const initials = (name || '?')
    .split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center shrink-0 relative">
      <span className="text-xs font-semibold text-gold">{initials}</span>
      {isVip ? (
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gold border border-[#0A0A0A] grid place-items-center shadow-glow-gold-sm">
          <Crown size={9} className="text-black" />
        </span>
      ) : null}
    </div>
  );
}

function StatusDot({ status, withLabel = true }) {
  const s = STATUS_DOT[status] || STATUS_DOT.inactive;
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="w-2.5 h-2.5 rounded-full"
        style={{ background: s.color, boxShadow: `0 0 8px ${s.color}80` }}
      />
      {withLabel ? <span className="text-xs text-white/75">{s.label}</span> : null}
    </span>
  );
}

function VipBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gold/20 border border-gold/40 text-[10px] font-bold text-gold uppercase tracking-wider"
      title="VIP customer"
    >
      <Crown size={9} /> VIP
    </span>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'export';
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
}

function digitsOnly(phone) {
  if (!phone) return '';
  return String(phone).replace(/[^\d]/g, '');
}

// WhatsApp templates ---------------------------------------------------------
const WA_TEMPLATES = [
  {
    id: 'welcome_back',
    label: 'Welcome back',
    icon: '👋',
    text: ({ name, brand_name }) =>
      `Hi ${name}, we've missed you at ${brand_name}! Stop by anytime — your loyalty card is waiting.`,
  },
  {
    id: 'reward_reminder',
    label: 'Reward reminder',
    icon: '🎯',
    // Falls back gracefully if we don't know stamps_remaining.
    text: ({ name, brand_name, stamps_remaining }) =>
      stamps_remaining != null && stamps_remaining > 0
        ? `Hi ${name}, you're ${stamps_remaining} stamp${
            stamps_remaining === 1 ? '' : 's'
          } away from your next reward at ${brand_name}!`
        : `Hi ${name}, your next reward at ${brand_name} is just around the corner — visit us this week to earn the final stamps!`,
  },
  {
    id: 'birthday_greeting',
    label: 'Birthday greeting',
    icon: '🎂',
    text: ({ name, brand_name }) =>
      `Happy birthday, ${name}! 🎉 ${brand_name} has a special gift waiting for you this month.`,
  },
  {
    id: 'promotion',
    label: 'Promotion',
    icon: '🎁',
    text: ({ name, brand_name }) =>
      `Hi ${name}, ${brand_name} has a new promotion you'll love. Visit us this week!`,
  },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CustomerCrmPage({ brandId, role }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const isAdmin = role === 'admin';

  // ---- filters / sort / pagination -----------------------------------------
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [statusFilter, setStatusFilter] = useState(null); // null | active | at_risk | inactive
  const [vipOnly, setVipOnly] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [showAdv, setShowAdv] = useState(false);
  const [spendMin, setSpendMin] = useState('');
  const [spendMax, setSpendMax] = useState('');
  const [lastVisitFrom, setLastVisitFrom] = useState('');
  const [lastVisitTo, setLastVisitTo] = useState('');
  const [includeFixture, setIncludeFixture] = useState(false);
  const [sortBy, setSortBy] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // ---- drawer / customer detail --------------------------------------------
  const [openCustomerId, setOpenCustomerId] = useState(null);

  // ---- bulk selection + delete state ---------------------------------------
  // Selection lives at the page level so it survives pagination/scroll. We
  // reset on brand switch (the visibility effect below already invalidates
  // the relevant queries). Mode flips between single (deleteTarget set) and
  // bulk (bulkDeleteOpen=true) — the modal renders accordingly.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visibleRows) => {
    setSelectedIds((prev) => {
      const allChecked = visibleRows.length > 0
        && visibleRows.every((r) => prev.has(r.customer_id));
      const next = new Set(prev);
      if (allChecked) {
        visibleRows.forEach((r) => next.delete(r.customer_id));
      } else {
        visibleRows.forEach((r) => next.add(r.customer_id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  // Reset selection on brand change (different CRM context).
  useEffect(() => { clearSelection(); }, [brandId]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, vipOnly, branchId, spendMin, spendMax,
      lastVisitFrom, lastVisitTo, includeFixture, pageSize, sortBy, sortDir]);

  // Cache-isolation: when brandId changes, drop stale caches under previous
  // brand to guarantee no flash of previous-brand data.
  useEffect(() => {
    if (!brandId) return;
    qc.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey)
        && (q.queryKey[0]?.startsWith('owner-crm-') || q.queryKey[0]?.startsWith('admin-crm-'))
        && q.queryKey[1] !== brandId,
    });
  }, [brandId, qc]);

  // ---- compose filter payload (one source of truth for queries + export) ---
  const filters = useMemo(() => ({
    search: search || undefined,
    branch_id: branchId || undefined,
    status: statusFilter || undefined,
    vip: vipOnly || undefined,
    spend_min: spendMin ? Number(spendMin) : undefined,
    spend_max: spendMax ? Number(spendMax) : undefined,
    last_visit_from: lastVisitFrom || undefined,
    last_visit_to: lastVisitTo || undefined,
    sort_by: sortBy, sort_dir: sortDir,
    page, page_size: pageSize,
    ...(isAdmin && includeFixture ? { include_fixture: true } : {}),
  }), [search, branchId, statusFilter, vipOnly, spendMin, spendMax,
      lastVisitFrom, lastVisitTo, sortBy, sortDir, page, pageSize,
      isAdmin, includeFixture]);

  // ---- queries -------------------------------------------------------------
  const listKey = isAdmin
    ? qk.adminCrmCustomers(brandId, filters)
    : qk.ownerCrmCustomers(brandId, filters);

  const listQ = useQuery({
    queryKey: listKey,
    queryFn: () =>
      isAdmin
        ? api.adminCrmCustomers(token, brandId, filters)
        : api.ownerCrmCustomers(token, filters),
    enabled: !!token && !!brandId,
    keepPreviousData: true,
    staleTime: 10_000,
  });

  const kpisKey = isAdmin ? qk.adminCrmKpis(brandId) : qk.ownerCrmKpis(brandId);
  const kpisQ = useQuery({
    queryKey: [...kpisKey, isAdmin && includeFixture ? 'fixtures' : 'live'],
    queryFn: () =>
      isAdmin
        ? api.adminCrmKpis(token, brandId, includeFixture)
        : api.ownerCrmKpis(token),
    enabled: !!token && !!brandId,
    staleTime: 15_000,
  });

  // Phase 5B.3 — brand_name now arrives on KPIs + customer detail and
  // brand_stamps_per_reward arrives on customer detail. No separate brand
  // fetch needed for header label or WhatsApp template substitution.

  // ---- branches (for the filter select) ----------------------------------
  // Owner reuses the existing /api/owner/branches endpoint. Admin uses the
  // Phase 5B.3 mirror /api/admin/brands/{brand_id}/branches so the filter
  // lists every branch in the brand (not just the ones surfaced by current
  // table rows).
  const branchesQ = useQuery({
    queryKey: qk.ownerBranches(brandId),
    queryFn: () =>
      isAdmin
        ? api.adminBrandBranches(token, brandId, isAdmin && includeFixture)
        : api.ownerBranches(token),
    enabled: !!token && !!brandId,
    staleTime: 60_000,
  });

  // ---- export mutation -----------------------------------------------------
  const [exporting, setExporting] = useState(false);

  // ---- delete mutations (Phase 5C.1) ---------------------------------------
  // Single & bulk share the same server contract — both invalidate the
  // 2-element list prefix + kpis (mirrors the Reset Stamps pattern that we
  // verified end-to-end). Admin role does NOT get a delete UI in this pass;
  // the mutation guards below short-circuit if isAdmin is true.
  const _afterDeleteSuccess = () => {
    qc.invalidateQueries({
      queryKey: [isAdmin ? 'admin-crm-customers' : 'owner-crm-customers', brandId],
      exact: false,
    });
    qc.invalidateQueries({ queryKey: kpisKey });
    setOpenCustomerId(null);
    clearSelection();
  };
  const singleDeleteM = useMutation({
    mutationFn: (cid) => api.ownerCrmDeleteCustomer(token, cid),
    onSuccess: (res) => {
      const c = res?.deleted_counts || {};
      const tx = c.transactions || 0;
      const cards = c.customer_cards || 0;
      toast.success(
        `Customer deleted (${cards} card, ${tx} transactions, `
        + `${c.stamp_history || 0} stamp events)`,
      );
      setDeleteTarget(null);
      _afterDeleteSuccess();
    },
    onError: (e) => {
      toast.error(e?.body?.detail?.message || e?.message || 'Delete failed');
    },
  });
  const bulkDeleteM = useMutation({
    mutationFn: (ids) => api.ownerCrmBulkDeleteCustomers(token, ids),
    onSuccess: (res) => {
      const ok = (res?.deleted || []).length;
      const failed = (res?.failed || []).length;
      if (failed === 0) toast.success(`${ok} customers deleted`);
      else toast.warning(`${ok} deleted, ${failed} skipped`);
      setBulkDeleteOpen(false);
      _afterDeleteSuccess();
    },
    onError: (e) => {
      toast.error(e?.body?.detail?.message || e?.message || 'Bulk delete failed');
    },
  });
  const doExport = async (format, opts = {}) => {
    if (exporting) return;
    setExporting(true);
    try {
      const exportFilters = {
        ...filters,
        format,
        // export endpoint ignores page/page_size — drop them
        page: undefined, page_size: undefined,
      };
      // 5B.3 — when `customer_id` is supplied, backend ignores every other
      // filter and emits a one-row file named with the customer's
      // membership_id.
      if (opts.customer_id) exportFilters.customer_id = opts.customer_id;
      const { blob, filename } = isAdmin
        ? await api.adminCrmExportBlob(token, brandId, exportFilters)
        : await api.ownerCrmExportBlob(token, exportFilters);
      downloadBlob(blob, filename);
      toast.success(opts.customer_id ? `Customer exported · ${filename}` : `Exported ${filename}`);
    } catch (e) {
      toast.error(e?.detail || e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ---- table state derivations ---------------------------------------------
  const rows = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const vipThreshold = listQ.data?.vip_spend_threshold_rupiah ?? kpisQ.data?.vip_spend_threshold_rupiah ?? 5_000_000;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(total, page * pageSize);

  const resetFilters = () => {
    setSearchInput(''); setStatusFilter(null); setVipOnly(false);
    setBranchId(''); setSpendMin(''); setSpendMax('');
    setLastVisitFrom(''); setLastVisitTo('');
    setSortBy('spend'); setSortDir('desc');
  };

  // ---- column sort toggle --------------------------------------------------
  const setSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  // brand_name now arrives on listQ + kpisQ (5B.3 additive). The detail
  // response also carries brand_stamps_per_reward; we read it inside the
  // drawer via `c.brand_stamps_per_reward`.
  const brandName = listQ.data?.brand_name || kpisQ.data?.brand_name || null;

  // ---- render --------------------------------------------------------------
  if (!brandId) {
    return (
      <EmptyState
        icon={<Users size={20} />}
        title="No brand context"
        subtitle="Pick a brand from the directory to view its CRM."
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* HEADER ------------------------------------------------------------- */}
      <PageHeader
        kicker={isAdmin ? 'Brand · Customer CRM' : 'Customer CRM'}
        title="Customer CRM"
        subtitle={
          isAdmin
            ? `Customer relationships for ${brandName ?? 'this brand'}.`
            : `Showing customers for ${brandName ?? 'your brand'}.`
        }
        actions={
          <ExportDropdown
            exporting={exporting}
            onExport={(fmt) => doExport(fmt)}
          />
        }
      />

      {/* KPIs --------------------------------------------------------------- */}
      <KpisRow
        kpisQ={kpisQ}
        isAdmin={isAdmin}
        onPickCustomer={(id) => setOpenCustomerId(id)}
      />

      {/* FILTER BAR --------------------------------------------------------- */}
      <SectionCard testID="crm-filter-bar">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[240px]">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
            />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, phone, or membership ID…"
              className="pl-9"
              data-testid="crm-search"
            />
          </div>

          {/* Status pills */}
          <div
            className="inline-flex p-1 bg-black/40 border border-white/8 rounded-xl"
            data-testid="crm-status-pills"
          >
            {[
              { v: null, label: 'All' },
              { v: 'active', label: 'Active', dot: STATUS_DOT.active.color },
              { v: 'at_risk', label: 'At risk', dot: STATUS_DOT.at_risk.color },
              { v: 'inactive', label: 'Inactive', dot: STATUS_DOT.inactive.color },
            ].map((p) => (
              <button
                key={p.label}
                onClick={() => setStatusFilter(p.v)}
                data-testid={`crm-status-${p.v ?? 'all'}`}
                className={
                  'px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5 '
                  + (statusFilter === p.v
                    ? 'bg-gold/20 text-gold border border-gold/30'
                    : 'text-white/60 hover:text-white border border-transparent')
                }
              >
                {p.dot ? (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: p.dot, boxShadow: `0 0 6px ${p.dot}80` }}
                  />
                ) : null}
                {p.label}
              </button>
            ))}
          </div>

          {/* VIP toggle */}
          <button
            type="button"
            onClick={() => setVipOnly((v) => !v)}
            data-testid="crm-vip-toggle"
            className={
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors '
              + (vipOnly
                ? 'bg-gold/20 text-gold border-gold/30 shadow-glow-gold-sm'
                : 'text-white/60 border-white/10 hover:text-white hover:border-white/20')
            }
          >
            <Crown size={12} /> VIP only
          </button>

          {/* Branch select */}
          <Select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="!w-auto min-w-[180px]"
            data-testid="crm-branch-filter"
          >
            <option value="">All branches</option>
            {(branchesQ.data || []).map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </Select>

          {/* Admin fixture toggle */}
          {isAdmin ? (
            <label
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-white/10 text-white/70 cursor-pointer hover:border-white/20"
              data-testid="crm-include-fixture-toggle"
            >
              <input
                type="checkbox"
                checked={includeFixture}
                onChange={(e) => setIncludeFixture(e.target.checked)}
                className="accent-gold"
              />
              Include test fixtures
            </label>
          ) : null}

          {/* Expand / collapse advanced filters */}
          <button
            type="button"
            onClick={() => setShowAdv((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"
            data-testid="crm-more-filters"
          >
            <Filter size={12} />
            More filters
            <ChevronDown size={12} className={showAdv ? 'rotate-180 transition' : 'transition'} />
          </button>

          {/* Reset */}
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto inline-flex items-center gap-1 text-xs text-white/40 hover:text-white"
            data-testid="crm-filter-reset"
          >
            <RefreshCw size={11} /> Reset
          </button>
        </div>

        {showAdv ? (
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/5"
            data-testid="crm-advanced-filters"
          >
            <FieldRow label="Min spend">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">Rp</span>
                <Input
                  type="number" min="0" step="10000"
                  value={spendMin}
                  onChange={(e) => setSpendMin(e.target.value)}
                  className="pl-9"
                  data-testid="crm-spend-min"
                />
              </div>
            </FieldRow>
            <FieldRow label="Max spend">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-xs">Rp</span>
                <Input
                  type="number" min="0" step="10000"
                  value={spendMax}
                  onChange={(e) => setSpendMax(e.target.value)}
                  className="pl-9"
                  data-testid="crm-spend-max"
                />
              </div>
            </FieldRow>
            <FieldRow label="Last visit from">
              <Input
                type="date"
                value={lastVisitFrom}
                onChange={(e) => setLastVisitFrom(e.target.value)}
                data-testid="crm-lastvisit-from"
              />
            </FieldRow>
            <FieldRow label="Last visit to">
              <Input
                type="date"
                value={lastVisitTo}
                onChange={(e) => setLastVisitTo(e.target.value)}
                data-testid="crm-lastvisit-to"
              />
            </FieldRow>
          </div>
        ) : null}
      </SectionCard>

      {/* BULK TOOLBAR (sticky, only when ≥1 row selected) ------------------- */}
      {selectedIds.size > 0 ? (
        <div
          data-testid="crm-bulk-toolbar"
          className="sticky top-2 z-10 flex items-center justify-between gap-3 rounded-2xl border border-gold/30 bg-[#1A1A1A]/95 backdrop-blur px-4 py-3 shadow-lg"
        >
          <div className="text-sm text-white">
            <span data-testid="crm-bulk-count" className="font-semibold text-gold">
              {selectedIds.size}
            </span>
            {selectedIds.size === 1 ? ' customer' : ' customers'} selected
            {selectedIds.size > 50 ? (
              <span className="ml-2 text-amber-300 text-xs">
                (only 50 may be deleted per request)
              </span>
            ) : null}
          </div>
          <div className="inline-flex items-center gap-2">
            <Button
              variant="subtle" size="sm"
              onClick={clearSelection}
              testID="crm-bulk-clear"
            >
              Clear selection
            </Button>
            <Button
              variant="danger" size="sm"
              leftIcon={<Trash2 size={14} />}
              onClick={() => setBulkDeleteOpen(true)}
              testID="crm-bulk-delete-btn"
              disabled={selectedIds.size === 0}
            >
              Delete {selectedIds.size} selected
            </Button>
          </div>
        </div>
      ) : null}

      {/* TABLE -------------------------------------------------------------- */}
      <CustomersTable
        listQ={listQ}
        rows={rows}
        vipThreshold={vipThreshold}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={setSort}
        onRowClick={(id) => setOpenCustomerId(id)}
        onExportOne={(row) => {
          // 5B.3 — backend supports `customer_id` for a one-row file
          // named with the membership_id; ignores other filters.
          doExport('csv', { customer_id: row.customer_id });
        }}
        selectedIds={selectedIds}
        onToggleOne={toggleSelectOne}
        onToggleAll={() => toggleSelectAll(rows)}
        onDeleteOne={(row) => setDeleteTarget(row)}
      />

      {/* PAGINATION --------------------------------------------------------- */}
      <Pagination
        page={page} totalPages={totalPages} total={total}
        pageStart={pageStart} pageEnd={pageEnd}
        pageSize={pageSize} setPageSize={setPageSize}
        setPage={setPage} loading={listQ.isFetching}
      />

      {/* PROFILE DRAWER ----------------------------------------------------- */}
      <CustomerDrawer
        brandId={brandId}
        brandName={brandName}
        customerId={openCustomerId}
        isAdmin={isAdmin}
        onClose={() => setOpenCustomerId(null)}
        vipThreshold={vipThreshold}
        onPhoneUpdated={() => {
          qc.invalidateQueries({ queryKey: listKey });
          qc.invalidateQueries({ queryKey: kpisKey });
        }}
      />

      {/* DELETE CONFIRMATION MODAL (single + bulk) -------------------------- */}
      <DeleteConfirmModal
        open={!!deleteTarget || bulkDeleteOpen}
        mode={bulkDeleteOpen ? 'bulk' : 'single'}
        target={deleteTarget}
        bulkRows={bulkDeleteOpen ? rows.filter((r) => selectedIds.has(r.customer_id)) : []}
        isAdmin={isAdmin}
        onCancel={() => { setDeleteTarget(null); setBulkDeleteOpen(false); }}
        onConfirm={() => {
          if (bulkDeleteOpen) {
            const ids = rows.filter((r) => selectedIds.has(r.customer_id)).map((r) => r.customer_id);
            bulkDeleteM.mutate(ids);
          } else if (deleteTarget) {
            singleDeleteM.mutate(deleteTarget.customer_id);
          }
        }}
        pending={singleDeleteM.isPending || bulkDeleteM.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPIs row
// ---------------------------------------------------------------------------
function KpisRow({ kpisQ, isAdmin, onPickCustomer }) {
  if (kpisQ.isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="crm-kpis-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <SectionCard key={i} padded><SkeletonLines rows={2} /></SectionCard>
        ))}
      </div>
    );
  }
  if (kpisQ.error) return <QueryError error={kpisQ.error} onRetry={() => kpisQ.refetch()} />;
  const k = kpisQ.data || {};
  const tops = k.top_spenders || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="crm-kpis">
        <KpiCard
          label="Active this month"
          value={(k.active_this_month ?? 0).toLocaleString('id-ID')}
          sub={`of ${(k.total_customers ?? 0).toLocaleString('id-ID')} total`}
          icon={<Users size={14} />}
          testID="crm-kpi-active"
        />
        <KpiCard
          label="At risk (30d)"
          value={(k.inactive_30d ?? 0).toLocaleString('id-ID')}
          sub="last visit 30–60 days"
          tone="slate"
          icon={<Calendar size={14} />}
          testID="crm-kpi-at-risk"
        />
        <KpiCard
          label="Inactive 60d+"
          value={(k.inactive_60d ?? 0).toLocaleString('id-ID')}
          sub="needs win-back"
          tone="red"
          icon={<XCircle size={14} />}
          testID="crm-kpi-inactive"
        />
        <KpiCard
          label="VIP customers"
          value={(k.vip_count ?? 0).toLocaleString('id-ID')}
          sub={`threshold ${formatRupiah(k.vip_spend_threshold_rupiah ?? 0)}`}
          icon={<Crown size={14} />}
          testID="crm-kpi-vip"
        />
        <KpiCard
          label="Repeat customers"
          value={`${(k.repeat_customer_pct ?? 0).toFixed(1)}%`}
          sub="2+ transactions"
          tone="green"
          icon={<Star size={14} />}
          testID="crm-kpi-repeat"
        />
      </div>

      {tops.length > 0 ? (
        <SectionCard testID="crm-top-spenders" padded>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-base font-semibold flex items-center gap-2">
                <Crown size={14} className="text-gold" />
                Top spenders
              </h3>
              <p className="text-xs text-white/45 mt-0.5">Top 5 by total spending — click a row to open profile.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
            {tops.map((t, i) => (
              <button
                key={t.customer_id}
                type="button"
                onClick={() => onPickCustomer(t.customer_id)}
                data-testid={`crm-top-spender-${i}`}
                className="text-left p-3 rounded-xl bg-black/30 border border-white/8 hover:border-gold/30 hover:bg-gold/5 transition group"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] text-gold font-mono font-bold">#{i + 1}</span>
                </div>
                <div className="font-semibold text-sm text-white truncate group-hover:text-gold transition">
                  {t.name}
                </div>
                <div className="text-[11px] text-white/40 font-mono mt-0.5">{t.membership_id}</div>
                <div className="text-sm text-gold font-display font-bold mt-1.5">
                  {formatRupiah(t.total_spending_rupiah)}
                </div>
              </button>
            ))}
          </div>
        </SectionCard>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export dropdown
// ---------------------------------------------------------------------------
function ExportDropdown({ exporting, onExport }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <Button
        variant="secondary" size="sm" loading={exporting}
        onClick={() => setOpen((o) => !o)} leftIcon={<Download size={12} />}
        rightIcon={<ChevronDown size={12} />}
        testID="crm-export-button"
      >
        Export
      </Button>
      {open ? (
        <div
          className="absolute right-0 top-full mt-1 min-w-[180px] rounded-xl border border-white/10 bg-[#1A1A1A] shadow-2xl z-30 py-1"
          data-testid="crm-export-menu"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); onExport('csv'); }}
            data-testid="crm-export-csv"
            className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onExport('xlsx'); }}
            data-testid="crm-export-xlsx"
            className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
          >
            Export Excel (.xlsx)
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customers table
// ---------------------------------------------------------------------------
function SortableTh({ label, col, sortBy, sortDir, onSort, align = 'left' }) {
  const active = sortBy === col;
  return (
    <Th align={align}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={
          'inline-flex items-center gap-1 hover:text-gold transition '
          + (active ? 'text-gold' : 'text-white/50')
        }
      >
        {label}
        <ArrowUpDown size={10} className={active ? 'opacity-100' : 'opacity-40'} />
        {active ? (
          <span className="text-[9px]">{sortDir === 'asc' ? '↑' : '↓'}</span>
        ) : null}
      </button>
    </Th>
  );
}

function CustomersTable({
  listQ, rows, vipThreshold, sortBy, sortDir, onSort, onRowClick, onExportOne,
  selectedIds, onToggleOne, onToggleAll, onDeleteOne,
}) {
  if (listQ.error) {
    return <QueryError error={listQ.error} onRetry={() => listQ.refetch()} />;
  }
  if (listQ.isLoading && !rows.length) {
    return (
      <SectionCard padded testID="crm-table-loading">
        <SkeletonLines rows={8} />
      </SectionCard>
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Users size={20} />}
        title="No customers match these filters"
        subtitle="Try clearing the filters or expanding the date range."
        testID="crm-table-empty"
      />
    );
  }

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.customer_id));
  const someSelected = !allSelected && rows.some((r) => selectedIds.has(r.customer_id));

  return (
    <Table testID="crm-customers-table">
      <THead>
        <Tr>
          <Th align="left" className="!w-10">
            <button
              type="button"
              aria-label={allSelected ? 'Deselect all' : 'Select all'}
              data-testid="crm-select-all"
              onClick={(e) => { e.stopPropagation(); onToggleAll(); }}
              className="w-6 h-6 grid place-items-center rounded text-white/60 hover:text-white"
            >
              {allSelected
                ? <SquareCheck size={16} className="text-gold" />
                : <Square size={16} className={someSelected ? 'text-gold/60' : ''} />}
            </button>
          </Th>
          <SortableTh label="Customer" col="name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <Th>Phone</Th>
          <Th>Membership ID</Th>
          <SortableTh label="Tx" col="transactions" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="Spending" col="spend" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <SortableTh label="Stamps" col="stamps" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
          <Th align="right">Cards</Th>
          <SortableTh label="Last visit" col="last_visit" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <Th>Favorite branch</Th>
          <Th align="right">Refs</Th>
          <Th>Birthday</Th>
          <Th>Status</Th>
          <Th align="right" className="!w-20" />
        </Tr>
      </THead>
      <TBody>
        {rows.map((r) => {
          const isChecked = selectedIds.has(r.customer_id);
          return (
          <Tr key={r.customer_id} onClick={() => onRowClick(r.customer_id)}>
            <Td>
              <button
                type="button"
                aria-label={isChecked ? 'Deselect customer' : 'Select customer'}
                data-testid={`crm-row-select-${r.customer_id}`}
                onClick={(e) => { e.stopPropagation(); onToggleOne(r.customer_id); }}
                className="w-6 h-6 grid place-items-center rounded text-white/50 hover:text-white"
              >
                {isChecked
                  ? <SquareCheck size={16} className="text-gold" />
                  : <Square size={16} />}
              </button>
            </Td>
            <Td>
              <div className="flex items-center gap-3 min-w-[200px]">
                <Initials name={r.name} isVip={r.is_vip} />
                <div className="min-w-0">
                  <div className="font-medium text-white truncate flex items-center gap-1.5">
                    {r.name}
                    {r.is_vip ? <VipBadge /> : null}
                  </div>
                  <div className="text-[11px] text-white/40 font-mono truncate">{r.email}</div>
                </div>
              </div>
            </Td>
            <Td>
              {r.phone
                ? <span className="font-mono text-xs text-white/80">{r.phone}</span>
                : <span className="text-white/30">—</span>}
            </Td>
            <Td><span className="font-mono text-xs text-gold/90">{r.membership_id || '—'}</span></Td>
            <Td align="right">{(r.total_transactions || 0).toLocaleString('id-ID')}</Td>
            <Td align="right">
              <span className={r.is_vip ? 'text-gold font-semibold' : 'text-white'}>
                {formatRupiah(r.total_spending_rupiah)}
              </span>
            </Td>
            <Td align="right">{(r.current_stamps || 0).toLocaleString('id-ID')}</Td>
            <Td align="right">{r.active_cards_count || 0}</Td>
            <Td>
              {r.last_visit_at ? (
                <span title={formatDateTime(r.last_visit_at)} className="text-white/85 text-xs">
                  {timeAgo(r.last_visit_at)}
                </span>
              ) : <span className="text-white/30">Never</span>}
            </Td>
            <Td>
              {r.favorite_branch?.name
                ? <span className="text-xs text-white/80 inline-flex items-center gap-1"><MapPin size={11} className="text-gold/70" />{r.favorite_branch.name}</span>
                : <span className="text-white/30">—</span>}
            </Td>
            <Td align="right">{r.referral_count || 0}</Td>
            <Td>
              {r.birthday_mmdd
                ? <span className="text-xs text-white/85 font-mono">{r.birthday_mmdd}</span>
                : <span className="text-white/30">—</span>}
            </Td>
            <Td><StatusDot status={r.activity_status} /></Td>
            <Td align="right">
              <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  aria-label="Delete customer"
                  data-testid={`crm-row-delete-${r.customer_id}`}
                  onClick={(e) => { e.stopPropagation(); onDeleteOne(r); }}
                  className="w-7 h-7 grid place-items-center rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10"
                  title="Delete customer"
                >
                  <Trash2 size={14} />
                </button>
                <RowActionsMenu
                  row={r}
                  onView={() => onRowClick(r.customer_id)}
                  onExport={() => onExportOne(r)}
                />
              </div>
            </Td>
          </Tr>
          );
        })}
      </TBody>
    </Table>
  );
}

function RowActionsMenu({ row, onView, onExport }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const onClick = (e, fn) => { e.stopPropagation(); setOpen(false); fn(); };
  return (
    <div className="relative" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label="Customer actions"
        onClick={() => setOpen((o) => !o)}
        data-testid={`crm-row-menu-${row.customer_id}`}
        className="w-7 h-7 grid place-items-center rounded-lg text-white/50 hover:text-white hover:bg-white/5"
      >
        <MoreHorizontal size={14} />
      </button>
      {open ? (
        <div className="absolute right-0 top-full mt-1 min-w-[180px] rounded-xl border border-white/10 bg-[#1A1A1A] shadow-2xl z-20 py-1 text-left">
          <MenuItem onClick={(e) => onClick(e, onView)}>View profile</MenuItem>
          <MenuItem
            disabled={!row.phone_available}
            onClick={(e) => onClick(e, () => {
              const tpl = WA_TEMPLATES[0].text({ name: row.name, brand_name: 'OneStamp' });
              const url = `https://wa.me/${digitsOnly(row.phone)}?text=${encodeURIComponent(tpl)}`;
              window.open(url, '_blank', 'noopener,noreferrer');
            })}
            disabledTooltip="No phone number on file"
          >
            WhatsApp
          </MenuItem>
          <MenuItem onClick={(e) => onClick(e, onExport)}>Export this customer</MenuItem>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ children, onClick, disabled, disabledTooltip }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={disabled ? disabledTooltip : undefined}
      className={
        'w-full text-left px-3 py-2 text-sm '
        + (disabled
          ? 'text-white/30 cursor-not-allowed'
          : 'text-white/80 hover:bg-white/5 hover:text-white')
      }
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
function Pagination({
  page, totalPages, total, pageStart, pageEnd, pageSize, setPageSize, setPage, loading,
}) {
  return (
    <div
      className="flex items-center justify-between flex-wrap gap-3 px-1"
      data-testid="crm-pagination"
    >
      <div className="text-xs text-white/50">
        Showing <span className="text-white/80">{pageStart.toLocaleString('id-ID')}</span>
        –<span className="text-white/80">{pageEnd.toLocaleString('id-ID')}</span> of
        {' '}<span className="text-white/80">{total.toLocaleString('id-ID')}</span> customers
        {loading ? <span className="ml-2 text-gold/70 animate-pulse">syncing…</span> : null}
      </div>
      <div className="flex items-center gap-3">
        <label className="text-xs text-white/50 inline-flex items-center gap-2">
          Page size
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            data-testid="crm-page-size"
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div className="inline-flex items-center gap-1">
          <Button
            variant="subtle" size="sm" disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            leftIcon={<ChevronLeft size={12} />}
            testID="crm-page-prev"
          >
            Prev
          </Button>
          <span className="px-2 text-xs text-white/60 min-w-[80px] text-center">
            Page <span className="text-white">{page}</span> of {totalPages}
          </span>
          <Button
            variant="subtle" size="sm" disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            rightIcon={<ChevronRight size={12} />}
            testID="crm-page-next"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer drawer (4 tabs)
// ---------------------------------------------------------------------------
function CustomerDrawer({
  brandId, brandName, customerId, isAdmin, onClose, vipThreshold, onPhoneUpdated,
}) {
  const open = !!customerId;
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [tab, setTab] = useState('overview');
  useEffect(() => { if (open) setTab('overview'); }, [customerId, open]);

  const detailKey = isAdmin
    ? qk.adminCrmCustomer(brandId, customerId)
    : qk.ownerCrmCustomer(brandId, customerId);

  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () =>
      isAdmin
        ? api.adminCrmCustomer(token, brandId, customerId)
        : api.ownerCrmCustomer(token, customerId),
    enabled: open && !!brandId && !!customerId,
  });

  const txQ = useQuery({
    queryKey: isAdmin
      ? qk.adminCrmCustomerTransactions(brandId, customerId, { limit: 20 })
      : qk.ownerCrmCustomerTransactions(brandId, customerId, { limit: 20 }),
    queryFn: () =>
      isAdmin
        ? api.adminCrmCustomerTransactions(token, brandId, customerId, { limit: 20 })
        : api.ownerCrmCustomerTransactions(token, customerId, { limit: 20 }),
    enabled: open && !!customerId && tab === 'transactions',
  });

  const rewardsQ = useQuery({
    queryKey: isAdmin
      ? qk.adminCrmCustomerRewards(brandId, customerId, { limit: 20 })
      : qk.ownerCrmCustomerRewards(brandId, customerId, { limit: 20 }),
    queryFn: () =>
      isAdmin
        ? api.adminCrmCustomerRewards(token, brandId, customerId, { limit: 20 })
        : api.ownerCrmCustomerRewards(token, customerId, { limit: 20 }),
    enabled: open && !!customerId && tab === 'rewards',
  });

  const c = detailQ.data;
  const effectiveBrandName = c?.brand_name || brandName || 'this brand';

  // Phone edit -----------------------------------------------------
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState(null);
  useEffect(() => {
    setEditingPhone(false); setPhoneError(null);
    setPhoneValue(c?.phone ?? '');
  }, [customerId, c?.phone]);

  const phoneM = useMutation({
    mutationFn: (val) =>
      isAdmin
        ? api.adminCrmPatchPhone(token, brandId, customerId, val)
        : api.ownerCrmPatchPhone(token, customerId, val),
    onSuccess: () => {
      toast.success('Phone updated');
      setEditingPhone(false); setPhoneError(null);
      qc.invalidateQueries({ queryKey: detailKey });
      onPhoneUpdated?.();
    },
    onError: (e) => {
      const code = e?.raw?.detail?.code;
      const rawDetail = e?.detail;
      // If the api wrapper fell back to "Request failed (NNN)" because the
      // response body got consumed by an external observability interceptor,
      // don't leak that into the UI — use our own friendly copy instead.
      const friendlyDetail = (typeof rawDetail === 'string' && /^Request failed \(\d+\)$/.test(rawDetail))
        ? null
        : rawDetail;
      if (e?.status === 409 || code === 'phone_already_in_use') {
        // Render backend's vague message verbatim (no identity leak).
        setPhoneError('This phone number is already in use.');
      } else if (e?.status === 422 || String(code || '').includes('phone_invalid')) {
        setPhoneError(friendlyDetail || 'Phone format is not recognised. Use 08… or +62… format.');
      } else {
        setPhoneError(friendlyDetail || 'Could not update phone.');
      }
    },
  });

  // Reset Stamps ----------------------------------------------------
  // Operator-grade utility: zeroes the customer's current_stamps and
  // total_stamps_earned for this brand. Server keeps transactions,
  // stamp_history, reward_redemptions, transaction_idempotency rows
  // intact. Same React Query keys are invalidated as the phone update
  // path so the overview re-renders with the new counters.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetError, setResetError] = useState(null);
  // L4 (2026-06-25) — Standalone Bonus grant modal state. Owner + SA
  // only (RBAC checked at button render time via `canGrantBonus`).
  const [standaloneBonusOpen, setStandaloneBonusOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const canGrantBonusHere = canGrantBonus(user);
  const resetM = useMutation({
    mutationFn: () =>
      isAdmin
        ? api.adminCrmResetPoints(token, brandId, customerId)
        : api.ownerCrmResetPoints(token, customerId),
    onSuccess: (res) => {
      setResetError(null);
      setResetOpen(false);
      toast.success(
        res?.already_zero
          ? 'Stamps already at 0 — no change'
          : `Stamps reset (was ${res?.previous?.current_stamps ?? '?'} / lifetime ${res?.previous?.total_stamps_earned ?? '?'})`,
      );
      qc.invalidateQueries({ queryKey: detailKey });
      qc.invalidateQueries({
        queryKey: [isAdmin ? 'admin-crm-customers' : 'owner-crm-customers', brandId],
        exact: false,
      });
    },
    onError: (e) => {
      // Inline error inside the modal — keep the modal open so the
      // operator can read what happened and dismiss explicitly.
      // Mirrors the phoneEdit error pattern in this file.
      const detail = typeof e?.detail === 'string' ? e.detail : null;
      setResetError(detail || 'Could not reset stamps');
    },
  });

  // Sticky footer actions ------------------------------------------
  // 5B.3: `brand_stamps_per_reward` is now on the detail payload, already
  // computed as the lowest reward threshold > current_stamps. Subtract for
  // the WhatsApp template's `{stamps_remaining}` slot.
  const stampsRemaining =
    c?.current_stamps != null && c?.brand_stamps_per_reward != null
      ? Math.max(0, c.brand_stamps_per_reward - c.current_stamps)
      : null;

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      testID="crm-drawer"
      title={
        c ? (
          <div className="flex items-center gap-2.5">
            <Initials name={c.name} isVip={c.is_vip} />
            <div className="min-w-0">
              <div className="text-base font-semibold truncate flex items-center gap-2">
                {c.name}
                {c.is_vip ? <VipBadge /> : null}
              </div>
              <div className="text-[11px] text-white/45 mt-0.5 inline-flex items-center gap-2">
                <StatusDot status={c.activity_status} />
                <span className="text-white/30">·</span>
                <span className="font-mono">{c.membership_id}</span>
              </div>
            </div>
          </div>
        ) : 'Customer'
      }
      footer={
        c ? (
          <>
            <Button
              variant="ghost" size="sm" onClick={onClose}
              testID="crm-drawer-close"
            >
              Close
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setResetOpen(true)}
              disabled={resetM.isPending}
              testID="crm-reset-stamps-btn"
            >
              Reset Stamps
            </Button>
            {canGrantBonusHere ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setStandaloneBonusOpen(true)}
                testID="crm-grant-standalone-bonus-btn"
              >
                Grant Standalone Bonus
              </Button>
            ) : null}
            <WhatsAppButton
              customer={c} brandName={effectiveBrandName}
              stampsRemaining={stampsRemaining}
            />
          </>
        ) : null
      }
    >
      {detailQ.isLoading ? <FullSpinner /> : null}
      {detailQ.error
        ? <QueryError error={detailQ.error} onRetry={() => detailQ.refetch()} />
        : null}
      {c ? (
        <>
          {/* Tabs ------------------------------------------------- */}
          <div className="flex items-center gap-1 border-b border-white/8 -mt-1 mb-5" data-testid="crm-drawer-tabs">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'transactions', label: 'Transactions' },
              { id: 'rewards', label: 'Rewards' },
              { id: 'referrals', label: 'Referrals' },
              { id: 'memberships', label: 'Memberships' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`crm-drawer-tab-${t.id}`}
                className={
                  'px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors '
                  + (tab === t.id
                    ? 'border-gold text-gold'
                    : 'border-transparent text-white/55 hover:text-white')
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' ? (
            <OverviewTab
              c={c}
              editingPhone={editingPhone}
              setEditingPhone={setEditingPhone}
              phoneValue={phoneValue}
              setPhoneValue={setPhoneValue}
              phoneError={phoneError}
              setPhoneError={setPhoneError}
              onSavePhone={() => phoneM.mutate(phoneValue.trim())}
              phoneSaving={phoneM.isPending}
              vipThreshold={vipThreshold}
            />
          ) : null}
          {tab === 'transactions'
            ? <TransactionsTab q={txQ} />
            : null}
          {tab === 'rewards'
            ? <RewardsTab q={rewardsQ} />
            : null}
          {tab === 'referrals'
            ? <ReferralsTab c={c} />
            : null}
          {tab === 'memberships'
            ? <MembershipsTab customer={c} />
            : null}
        </>
      ) : null}
    </Drawer>
    <Modal
      open={resetOpen}
      onClose={() => { if (!resetM.isPending) { setResetOpen(false); setResetError(null); } }}
      title="Reset Stamps"
      size="sm"
      testID="crm-reset-modal"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => { setResetOpen(false); setResetError(null); }}
            disabled={resetM.isPending}
            testID="crm-reset-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => { setResetError(null); resetM.mutate(); }}
            loading={resetM.isPending}
            testID="crm-reset-confirm"
          >
            Reset
          </Button>
        </>
      }
    >
      <div className="space-y-2 text-sm">
        <p className="text-white/85">Reset this customer&apos;s stamps to 0?</p>
        <p className="text-xs text-white/50">
          This cannot be undone. Transaction history will be preserved.
        </p>
        {resetError ? (
          <div
            className="text-xs text-red-400 pt-2"
            data-testid="crm-reset-error"
          >
            {resetError}
          </div>
        ) : null}
      </div>
    </Modal>

    {/* L4 (2026-06-25) — Standalone Bonus grant modal (Owner + SA). */}
    <BonusStampModal
      open={standaloneBonusOpen}
      onClose={() => setStandaloneBonusOpen(false)}
      mode="standalone"
      context={{
        customerId,
        customerName: c?.name,
        brandId,
        brandName: effectiveBrandName,
      }}
      onGranted={() => {
        // A fresh BONUS-type transaction just landed for this
        // customer's card at this brand. Refresh detail + tx list +
        // owner-side dashboards so the numbers reflect the new
        // stamps (final_stamp bump on the card) instantly.
        qc.invalidateQueries({ queryKey: detailKey });
        qc.invalidateQueries({
          queryKey: isAdmin
            ? qk.adminCrmCustomerTransactions(brandId, customerId, { limit: 20 })
            : qk.ownerCrmCustomerTransactions(brandId, customerId, { limit: 20 }),
        });
        qc.invalidateQueries({
          queryKey: [isAdmin ? 'admin-crm-customers' : 'owner-crm-customers', brandId],
          exact: false,
        });
        qc.invalidateQueries({
          queryKey: ['owner-recent-activity'],
          exact: false,
        });
      }}
    />
    </>
  );
}

function OverviewTab({
  c, editingPhone, setEditingPhone, phoneValue, setPhoneValue,
  phoneError, setPhoneError, onSavePhone, phoneSaving, vipThreshold,
}) {
  const spendPct = vipThreshold > 0
    ? Math.min(100, Math.round(((c.total_spending_rupiah || 0) / vipThreshold) * 100))
    : 0;
  return (
    <div className="space-y-5" data-testid="crm-drawer-overview">
      {/* Phone with inline edit */}
      <div className="rounded-2xl border border-white/8 bg-black/30 p-4" data-testid="crm-phone-row">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">Phone</span>
          {!editingPhone ? (
            <button
              type="button"
              onClick={() => { setPhoneError(null); setEditingPhone(true); }}
              className="text-[11px] text-gold hover:underline inline-flex items-center gap-1"
              data-testid="crm-phone-edit-toggle"
            >
              <Pencil size={11} /> Edit
            </button>
          ) : null}
        </div>
        {!editingPhone ? (
          <div className="mt-1.5 font-mono text-sm text-white">
            {c.phone || <span className="text-white/30">No phone on file</span>}
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            <Input
              value={phoneValue}
              onChange={(e) => setPhoneValue(e.target.value)}
              placeholder="e.g. 08123456789 or +6281234567890"
              data-testid="crm-phone-input"
            />
            {phoneError ? (
              <div className="text-xs text-red-400" data-testid="crm-phone-error">{phoneError}</div>
            ) : (
              <div className="text-[11px] text-white/40">We'll normalise to E.164 (+62…).</div>
            )}
            <div className="flex items-center gap-2">
              <Button
                size="sm" loading={phoneSaving}
                onClick={onSavePhone}
                testID="crm-phone-save"
              >
                Save phone
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => { setEditingPhone(false); setPhoneError(null); setPhoneValue(c.phone || ''); }}
                testID="crm-phone-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* VIP progress bar */}
      <div className="rounded-2xl border border-white/8 bg-black/30 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
            VIP progress
          </span>
          {c.is_vip ? <VipBadge /> : (
            <span className="text-[11px] text-white/50">{spendPct}% of threshold</span>
          )}
        </div>
        <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${spendPct}%`,
              background: c.is_vip
                ? 'linear-gradient(90deg, #C9A84C, #F0D080)'
                : 'rgba(201,168,76,0.5)',
            }}
          />
        </div>
        <div className="flex justify-between text-[11px] text-white/45 mt-1.5">
          <span>{formatRupiah(c.total_spending_rupiah)}</span>
          <span>{formatRupiah(vipThreshold)}</span>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3" data-testid="crm-overview-stats">
        <Stat k="Total transactions" v={(c.total_transactions ?? 0).toLocaleString('id-ID')} />
        <Stat k="Total stamps earned" v={(c.total_stamps_earned ?? 0).toLocaleString('id-ID')} />
        <Stat k="Active cards" v={c.active_cards_count ?? 0} />
        <Stat k="Current stamps" v={c.current_stamps ?? 0} />
        <Stat k="Last visit" v={c.last_visit_at ? formatDateTime(c.last_visit_at) : 'Never'} />
        <Stat k="Joined" v={c.joined_at ? formatDate(c.joined_at) : '—'} />
        <Stat k="Favorite branch" v={c.favorite_branch?.name || '—'} />
        <Stat k="Referrals" v={c.referral_count ?? 0} />
        <Stat k="Birthday" v={c.birthday_mmdd || '—'} />
        <Stat k="Email" v={<span className="font-mono text-xs">{c.email || '—'}</span>} />
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{k}</div>
      <div className="text-sm text-white mt-1">{v}</div>
    </div>
  );
}

function TransactionsTab({ q }) {
  const [openTxn, setOpenTxn] = useState(null);
  if (q.isLoading) return <FullSpinner />;
  if (q.error) return <QueryError error={q.error} onRetry={() => q.refetch()} />;
  const items = q.data?.items || [];
  if (!items.length) {
    return <EmptyState title="No transactions yet." subtitle="Stamps earned for this customer will appear here." />;
  }
  return (
    <>
      <div className="space-y-2" data-testid="crm-drawer-transactions">
        {items.map((t) => {
          // L4 (2026-06-25) — surface the L1/L2 triple + type
          // discriminator. Legacy pre-L1 rows: fall back to
          // `stamps_earned` for `final_stamp`, treat bonus as 0.
          const isBonus = t.type === 'BONUS';
          const finalStamp = t.final_stamp != null
            ? Number(t.final_stamp)
            : Number(t.stamps_earned ?? 0);
          const bonusStamp = t.bonus_stamp != null ? Number(t.bonus_stamp) : 0;
          const sourceLabel = isBonus && Array.isArray(t.bonus_adjustments) && t.bonus_adjustments[0]
            ? BONUS_SOURCE_LABEL[t.bonus_adjustments[0].source] ?? t.bonus_adjustments[0].source
            : null;
          const shellCls = isBonus
            ? 'rounded-xl border border-gold/25 bg-gold/8 p-3 flex items-center gap-3 cursor-pointer hover:bg-gold/12 transition-colors'
            : 'rounded-xl border border-white/8 bg-black/30 p-3 flex items-center gap-3 cursor-pointer hover:border-white/15 transition-colors';
          return (
            <div
              key={t.id}
              className={shellCls}
              data-testid={isBonus ? 'crm-tx-row-bonus' : 'crm-tx-row-purchase'}
              onClick={() => setOpenTxn(t)}
            >
              <div className={`w-9 h-9 rounded-xl border grid place-items-center shrink-0 ${
                isBonus
                  ? 'bg-gold/25 border-gold/50 text-gold'
                  : 'bg-gold/15 border-gold/30 text-gold'
              }`}>
                {isBonus ? <Gift size={14} /> : <Calendar size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate flex items-center gap-2 flex-wrap">
                  {isBonus ? (
                    <>
                      <span className="text-gold font-semibold">
                        Bonus granted{sourceLabel ? ` — ${sourceLabel}` : ''}
                      </span>
                      <span className="text-white/70">
                        +{finalStamp} stamp{finalStamp === 1 ? '' : 's'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>{formatRupiah(t.amount_rupiah)}</span>
                      <span className="text-white/40"> · </span>
                      <span className="text-white/70">
                        {finalStamp} stamp{finalStamp === 1 ? '' : 's'}
                      </span>
                      {bonusStamp > 0 ? (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border border-gold/40 bg-gold/12 text-gold"
                          data-testid="crm-tx-bonus-chip"
                        >
                          +{bonusStamp} bonus
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
                <div className="text-[11px] text-white/40 mt-0.5 truncate">
                  {isBonus
                    ? 'Standalone bonus · no purchase, no receipt'
                    : (
                      <>
                        {t.branch_name || '—'}
                        {t.invoice_number ? (
                          <>
                            {' · '}
                            <span className="font-mono">{t.invoice_number}</span>
                          </>
                        ) : null}
                      </>
                    )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] text-white/60">{formatDateTime(t.created_at)}</div>
                {!isBonus && t.receipt_url ? (
                  <a
                    href={t.receipt_url} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] text-gold hover:underline mt-0.5 inline-block"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Receipt
                  </a>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {openTxn ? (
        <TransactionDetailsModal
          tx={{
            // Adapt CRM-transaction shape to the modal's expected
            // activity-style props. The modal already tolerates both
            // `created_at` and `at`, and derives L1/L2 fields with
            // legacy fallbacks — this adapter just adds the fields
            // that are named differently in the two endpoints.
            ...openTxn,
            at: openTxn.created_at,
            customer_name: openTxn.customer_name,
            branch_name: openTxn.branch_name,
            // Surface the BONUS-vs-PURCHASE discriminator using the
            // key the modal reads for its own tracking (`txn_type` —
            // recent-activity uses this so `type` can stay for the
            // activity-row discriminator).
            txn_type: openTxn.type,
          }}
          onClose={() => setOpenTxn(null)}
        />
      ) : null}
    </>
  );
}

function RewardsTab({ q }) {
  if (q.isLoading) return <FullSpinner />;
  if (q.error) return <QueryError error={q.error} onRetry={() => q.refetch()} />;
  const items = q.data?.items || [];
  if (!items.length) {
    return <EmptyState title="No redemptions yet." subtitle="When this customer redeems a reward, it'll show here." />;
  }
  return (
    <div className="space-y-2" data-testid="crm-drawer-rewards">
      {items.map((r) => (
        <div key={r.id} className="rounded-xl border border-white/8 bg-black/30 p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 grid place-items-center text-emerald-300 shrink-0">
            <Gift size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{r.reward_title || 'Reward redemption'}</div>
            <div className="text-[11px] text-white/40 mt-0.5">
              {r.stamps_used} stamp{r.stamps_used === 1 ? '' : 's'} · {r.branch_name || '—'}
            </div>
          </div>
          <div className="text-[11px] text-white/60 shrink-0">{formatDateTime(r.redeemed_at)}</div>
        </div>
      ))}
    </div>
  );
}

function ReferralsTab({ c }) {
  return (
    <div className="space-y-3" data-testid="crm-drawer-referrals">
      <div className="rounded-2xl border border-white/8 bg-black/30 p-4">
        <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
          Total completed referrals
        </div>
        <div className="text-3xl font-display font-bold text-gold mt-1">
          {c.referral_count ?? 0}
        </div>
        <div className="text-xs text-white/50 mt-1">
          Completed signups attributed to this customer's referral code.
        </div>
      </div>
      <p className="text-xs text-white/40">
        Per-referral history (invitee names, conversion dates, status) will land in a follow-up release.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Memberships tab — Phase 1
// Lists paid memberships for this (customer, brand) pair using the cashier
// endpoint (accepts brand_owner + super_admin too; auto-scoped via JWT).
// Exposes an "Assign Membership" CTA that hydrates AssignMembershipModal
// with the customer prefilled.
// ---------------------------------------------------------------------------
function MembershipsTab({ customer }) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const ownerBrandId = user?.brand_id;
  // CRM detail responses use `customer_id` instead of `id` — defensive lookup.
  const customerId = customer?.id || customer?.customer_id || null;
  const [assignOpen, setAssignOpen] = useState(false);
  const queryClient = useQueryClient();

  const q = useQuery({
    queryKey: qk.ownerCustomerMemberships(customerId),
    queryFn: () => api.ownerCustomerMemberships(token, customerId),
    enabled: !!token && !!customerId,
  });

  const rows = q.data ?? [];
  const active = rows.filter((m) => m.effective_status === 'active');
  const history = rows.filter((m) => m.effective_status !== 'active');

  const refreshAfterAssign = () => {
    queryClient.invalidateQueries({ queryKey: qk.ownerCustomerMemberships(customerId) });
    queryClient.invalidateQueries({ queryKey: qk.ownerMembershipMembers(ownerBrandId) });
    queryClient.invalidateQueries({ queryKey: qk.ownerMembershipDashboard(ownerBrandId) });
  };

  return (
    <div className="space-y-4" data-testid="crm-drawer-memberships">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
            Memberships
          </div>
          <div className="text-[11px] text-white/40 mt-0.5">
            Paid memberships at this brand. Active first, history below.
          </div>
        </div>
        <Button
          variant="primary" size="sm"
          onClick={() => setAssignOpen(true)}
          testID="crm-memberships-assign-button"
          disabled={!ownerBrandId}
        >
          Assign Membership
        </Button>
      </div>

      {q.isLoading ? <FullSpinner /> : null}
      {q.error ? <QueryError error={q.error} onRetry={() => q.refetch()} /> : null}

      {!q.isLoading && !q.error && rows.length === 0 ? (
        <EmptyState
          testID="crm-memberships-empty"
          title="No memberships yet"
          subtitle="This customer hasn't purchased a paid membership at your brand."
          action={
            <Button
              variant="secondary" size="sm"
              onClick={() => setAssignOpen(true)}
              testID="crm-memberships-empty-assign"
            >
              Assign Membership
            </Button>
          }
        />
      ) : null}

      {active.length > 0 ? (
        <div
          className="space-y-3"
          data-testid="crm-memberships-active-list"
        >
          {active.map((m) => (
            <CrmMembershipCard key={m.id} m={m} variant="active" />
          ))}
        </div>
      ) : null}

      {history.length > 0 ? (
        <div data-testid="crm-memberships-history-list">
          <div className="text-[11px] uppercase tracking-wider text-white/35 font-mono mb-2 mt-4">
            History
          </div>
          <div className="space-y-2">
            {history.map((m) => (
              <CrmMembershipCard key={m.id} m={m} variant="history" />
            ))}
          </div>
        </div>
      ) : null}

      <AssignMembershipModal
        open={assignOpen}
        onClose={() => {
          setAssignOpen(false);
          refreshAfterAssign();
        }}
        brandId={ownerBrandId}
        customerId={customer.id}
        customerLabel={`${customer.name || customer.id} · ${customer.membership_id || '—'}`}
      />
    </div>
  );
}

function CrmMembershipCard({ m, variant }) {
  const tierColor = m.tier?.display_color || '#C9A84C';
  const sp = MEMBERSHIP_STATUS_PILL[m.effective_status] || MEMBERSHIP_STATUS_PILL.cancelled;
  const dim = variant === 'history';
  return (
    <div
      data-testid={`crm-membership-row-${m.id}`}
      className={
        'rounded-2xl border p-4 bg-black/30 transition-colors '
        + (dim ? 'opacity-70' : 'hover:border-gold/30')
      }
      style={dim ? { borderColor: 'rgba(255,255,255,0.06)' } : { borderColor: `${tierColor}40` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{m.plan?.name || '—'}</span>
            {m.tier ? <TierBadge tier={m.tier} /> : null}
            {!m.expiry_date ? <StatusPill variant="gold">Lifetime</StatusPill> : null}
          </div>
          <div className="text-[11px] text-white/45 mt-1 font-mono">
            Started {fmtDate(m.start_date)}
            {m.expiry_date ? ` · ${expiryLabel(m)}` : ''}
          </div>
          {m.cancelled_at ? (
            <div className="text-[11px] text-rose-300/80 mt-1">
              Cancelled {fmtDate(m.cancelled_at)}
              {m.cancellation_reason ? <> · <span className="font-mono">{m.cancellation_reason}</span></> : null}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusPill variant={sp.variant} testID={`crm-membership-status-${m.id}`}>
            {sp.label}
          </StatusPill>
          <div className="text-xs text-gold font-mono">{rupiah(m.price_paid)}</div>
        </div>
      </div>

      {(m.benefits ?? []).length > 0 && variant === 'active' ? (
        <div className="mt-3 pt-3 border-t border-white/8">
          <div className="text-[10px] uppercase tracking-wider text-gold/70 font-semibold mb-1.5">
            Benefits
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
            {m.benefits.slice(0, 4).map((b) => (
              <li key={b.id} className="text-xs text-white/80 flex gap-1.5">
                <span className="text-gold">•</span>
                <span className="truncate">{b.name}</span>
              </li>
            ))}
          </ul>
          {m.benefits.length > 4 ? (
            <div className="text-[10px] text-white/40 italic mt-1.5">
              +{m.benefits.length - 4} more
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WhatsApp button + template popover
// ---------------------------------------------------------------------------
function WhatsAppButton({ customer, brandName, stampsRemaining }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const phoneOk = !!customer.phone_available;

  const send = (tpl) => {
    const text = tpl.text({
      name: customer.name?.split(' ')[0] || customer.name || 'there',
      brand_name: brandName,
      stamps_remaining: stampsRemaining,
    });
    const url = `https://wa.me/${digitsOnly(customer.phone)}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="primary"
        size="md"
        disabled={!phoneOk}
        title={phoneOk ? undefined : 'No phone number on file'}
        onClick={() => setOpen((o) => !o)}
        leftIcon={<MessageCircle size={14} />}
        testID="crm-whatsapp-button"
      >
        WhatsApp
      </Button>
      {open && phoneOk ? (
        <div
          className="absolute right-0 bottom-full mb-2 w-[320px] rounded-2xl border border-white/10 bg-[#0E0E13] shadow-2xl z-30 p-2"
          data-testid="crm-whatsapp-popover"
        >
          <div className="px-2 pt-1 pb-2 border-b border-white/5">
            <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
              Pick a template
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">
              Opens WhatsApp in a new tab with the message pre-filled.
            </div>
          </div>
          <div className="py-1.5 space-y-1">
            {WA_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => send(t)}
                data-testid={`crm-whatsapp-template-${t.id}`}
                className="w-full text-left px-3 py-2 rounded-xl hover:bg-gold/10 hover:border-gold/20 border border-transparent transition group"
              >
                <div className="text-sm text-white font-medium flex items-center gap-2">
                  <span>{t.icon}</span> {t.label}
                </div>
                <div className="text-[11px] text-white/45 mt-0.5 line-clamp-2 group-hover:text-white/65">
                  {t.text({
                    name: customer.name?.split(' ')[0] || customer.name || 'there',
                    brand_name: brandName,
                    stamps_remaining: stampsRemaining,
                  })}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Delete confirmation modal (single + bulk)
// ---------------------------------------------------------------------------
function DeleteConfirmModal({
  open, mode, target, bulkRows, isAdmin, onCancel, onConfirm, pending,
}) {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (!open) setTyped(''); }, [open]);

  // Hard-clamp bulk to the server cap so the UI mirrors what the server
  // will accept. Server also enforces it (defense-in-depth).
  const BULK_CAP = 50;
  const isBulk = mode === 'bulk';
  const rows = isBulk ? (bulkRows || []) : (target ? [target] : []);
  const overCap = isBulk && rows.length > BULK_CAP;
  const okToDelete = typed === 'DELETE' && rows.length > 0 && !overCap && !pending && !isAdmin;

  // Aggregate impact summary across the rows (best-effort from already-fetched
  // list payload; lifetime aggregates are by design from immutable history).
  const totals = rows.reduce((acc, r) => {
    acc.cards += (r.active_cards_count || 0);
    acc.tx += (r.total_transactions || 0);
    acc.stamps += (r.total_stamps_earned || 0);
    return acc;
  }, { cards: 0, tx: 0, stamps: 0 });

  if (!open) return null;
  const title = isBulk
    ? `Delete ${rows.length} ${rows.length === 1 ? 'customer' : 'customers'}`
    : 'Delete customer';
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      testID="crm-delete-modal"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost" size="sm"
            onClick={onCancel}
            testID="crm-delete-cancel"
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            variant="danger" size="sm"
            onClick={onConfirm}
            testID="crm-delete-confirm"
            disabled={!okToDelete}
            leftIcon={<Trash2 size={14} />}
          >
            {pending ? 'Deleting…' : (isBulk ? `Delete ${rows.length}` : 'Delete')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        {isAdmin ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-200 text-xs">
            Customer delete is owner-only in this release.
          </div>
        ) : null}
        {/* Names list (first 5 + "and N more") */}
        <div data-testid="crm-delete-names" className="text-white">
          {rows.slice(0, 5).map((r) => (
            <div key={r.customer_id || r.id} className="text-sm">
              <span className="font-medium">{r.name}</span>
              {r.membership_id ? (
                <span className="ml-2 text-xs text-gold/80 font-mono">({r.membership_id})</span>
              ) : null}
            </div>
          ))}
          {rows.length > 5 ? (
            <div className="text-xs text-white/50 mt-1">
              and {rows.length - 5} more
            </div>
          ) : null}
        </div>
        {/* Plain-English semantic */}
        <div className="text-xs text-white/55">
          This action cannot be undone. The customer will be unlinked from this
          brand only — their account and data at other brands are preserved.
        </div>
        {/* Impact summary */}
        <div className="text-xs text-white/70" data-testid="crm-delete-impact">
          Will delete: <span className="text-white">{totals.cards}</span> card{totals.cards === 1 ? '' : 's'},
          {' '}<span className="text-white">{totals.tx.toLocaleString('id-ID')}</span> transaction{totals.tx === 1 ? '' : 's'},
          {' '}<span className="text-white">{totals.stamps.toLocaleString('id-ID')}</span> stamp event{totals.stamps === 1 ? '' : 's'}.
        </div>
        {overCap ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200 text-xs">
            Selection exceeds the {BULK_CAP}-customer-per-request cap.
            Please reduce the selection.
          </div>
        ) : null}
        {/* Type-to-confirm */}
        <div className="pt-1">
          <label className="text-xs text-white/55 block mb-1">
            Type <span className="font-mono text-gold">DELETE</span> to confirm
          </label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="DELETE"
            autoFocus
            data-testid="crm-delete-typed"
            disabled={pending}
          />
        </div>
      </div>
    </Modal>
  );
}
